import {loadStripe, Stripe, StripeElements} from '@stripe/stripe-js';

import * as utils from '../js/utils';
import * as api from '../js/api';

interface PWindow extends Window {
    pay: {
        api: string
        stripe_publishable_key: string,
    }
}
declare const window: PWindow;

interface CartItem {
    quantity: number
}

class Cart {
    element: HTMLElement;

    constructor() {
        this.element = this.getElement();
    }

    private getElement(): HTMLElement {
        return document.querySelector('.payments-cart') as HTMLElement;
    }

    private disableSelects() {
        this.element.querySelectorAll('select').forEach((element) => {
            element.disabled = true;
        });
    }

    private enableSelects() {
        this.element.querySelectorAll('select').forEach((element) => {
            element.disabled = false;
        });
    }

    private static itemUpdate(price_id: string, quantity: number): Promise<{cart: {items: CartItem[]}}> {
        return api.patch('/cart/items', {
            price_id: price_id, quantity: quantity
        }) as unknown as Promise<{cart: {items: CartItem[]}}>;
    }

    private static template(): Promise<{template: string}> {
        return api.get('/templates/cart' ) as unknown as Promise<{template: string}>;
    }

    public async updateQuantity(event: Event) {
        this.disableSelects();
        document.dispatchEvent( new CustomEvent('payments-pre-update-quantity' ) );

        const target = event.target as HTMLSelectElement;
        const price_id = target.dataset.price as string;

        try {
            const {cart} = await Cart.itemUpdate(price_id, parseInt(target.value));
            if (cart.items.length > 0) {
                const {template} = await Cart.template();
                this.element.outerHTML = template;
                document.dispatchEvent( new CustomEvent('payments-update-quantity', {
                    detail: {
                        price_id: price_id,
                        quantity: target.value,
                    }
                }) );
            } else {
                window.location.reload();
            }

            this.element = this.getElement();
            this.bind();
        } catch (e) {
            utils.showMessage({form: this.element, message: utils.getErrorMessage(e)});
        }

        this.enableSelects();
    }

    public bind() {
        if (this.element) {
            this.element.querySelectorAll('[data-action="update-quantity"]').forEach((element) => {
                element.addEventListener('change', this.updateQuantity.bind(this));
            });
        }
    }
}


class CheckoutForm {
    private static stripe: Stripe;
    private readonly elements: StripeElements;
    private readonly form: HTMLFormElement;

    constructor(elements: StripeElements, form: HTMLFormElement) {
        this.elements = elements;
        this.form = form;
    }

    private static returnUrl(props: { return_url: string }): string {
        const {api} = window.pay;
        return `${ api }/checkout/confirm?return_url=${ encodeURIComponent(props.return_url) }`;
    }

    private static processData(parent: Element, name: string, value: string) {
        parent.querySelectorAll(`[data-pay-data="${ name }"]`).forEach((element) => {
            element.textContent = value;
        });
    }

    private static async checkoutApi(): Promise<{ client_secret: string, amount: string }> {
        if (!CheckoutForm.stripe) {
            return await Promise.all([api.get('/checkout'), loadStripe(window.pay.stripe_publishable_key)])
                .then(values => {
                    if (!values[1]) {
                        throw 'Stripe failed to load.';
                    }
                    CheckoutForm.stripe = values[1];
                    return values[0] as unknown as { client_secret: string, amount: string };
                });
        }
        return await api.get('/checkout') as unknown as Promise<{ client_secret: string, amount: string }>;
    }

    public static async build(form: HTMLFormElement): Promise<CheckoutForm> {

        try {
            const res = await this.checkoutApi();
            const elements = CheckoutForm.stripe.elements({
                clientSecret: res.client_secret,
                appearance: utils.stripeAppearance({form})
            });


            const paymentElement = elements.create('payment');
            paymentElement.mount('#payment-element');
            CheckoutForm.processData(form, 'amount', res.amount);
            form.classList.remove('payments-hidden');

            return new CheckoutForm(elements, form);
        } catch (e) {
            utils.showMessage({form: form.parentElement, message: utils.getErrorMessage(e)});
            throw(e);
        }
    }

    /*
     * Fetches the payment intent status after payment submission.
     *
     * This function only does anything stripe redirected back to the original page.
     * The normal workflow does not do so.
     */
    public async checkStatus(): Promise<void> {
        const clientSecret = new URLSearchParams(window.location.search).get(
            'payment_intent_client_secret'
        );

        if (!clientSecret) {
            return;
        }

        const {paymentIntent} = await CheckoutForm.stripe.retrievePaymentIntent(clientSecret);

        if (paymentIntent) {
            switch (paymentIntent.status) {
            case 'succeeded':
                utils.showMessage({form: this.form, message: 'Payment succeeded!'});
                break;
            case 'processing':
                utils.showMessage({form: this.form, message: 'Your payment is processing.'});
                break;
            case 'requires_payment_method':
                utils.showMessage({form: this.form, message: 'Your payment was not successful, please try again.'});
                break;
            default:
                utils.showMessage({form: this.form, message: 'Something went wrong.'});
                break;
            }
        }
    }

    public async validate(): Promise<boolean> {
        const body = utils.formData({form: this.form});
        body['_form'] = 'checkout';

        utils.clearFieldErrors({parent: this.form});

        try {
            await api.post('/validation', body);
            return true;
        } catch (e) {
            if (e instanceof api.HttpError) {
                const {messages, fields} = e.res.errors;
                if (messages) {
                    utils.showMessage({form: this.form, message: messages});
                }

                if (fields) {
                    for (const [key, value] of Object.entries(fields)) {
                        const input = this.form.querySelector(`input[name="${ key }"]`);
                        if (input) {
                            utils.setFieldError({input, message: value});
                        }
                    }
                }
            }
            utils.getErrorMessage(e);
        }
        return false;
    }

    public async disable() {
        utils.setLoading({form: this.form, isLoading: true});
    }

    public async update() {
        const data = await CheckoutForm.checkoutApi();
        const elements = CheckoutForm.stripe.elements({
            clientSecret: data.client_secret,
            appearance: utils.stripeAppearance({form: this.form})
        });

        const paymentElement = elements.create('payment');
        paymentElement.mount('#payment-element');
        CheckoutForm.processData(this.form, 'amount', data.amount);
        utils.setLoading({form: this.form, isLoading: false});
    }

    public async submit(event: Event) {
        event.preventDefault();
        utils.setLoading({form: event.target, isLoading: true});

        /*
         * All field validation.
         */
        if (!await this.validate()) {
            utils.setLoading({form: event.target, isLoading: false});
            return;
        }

        /*
         * Setup the payment.
         * The backend figures out how to charge the customer given the contents of their cart,
         * then sends back a client secret to confirm the payment.
         */

        // TBD: make configurable.
        const return_url = new URL(window.location.href);
        return_url.searchParams.set('payments_status', 'success');

        const {error} = await CheckoutForm.stripe.confirmPayment({
            elements: this.elements,
            confirmParams: {
                return_url: CheckoutForm.returnUrl({return_url: return_url.href}),
            },
        });

        // This point will only be reached if there is an immediate error when
        // confirming the payment. Otherwise, your customer will be redirected to
        // your `return_url`. For some payment methods like iDEAL, your customer will
        // be redirected to an intermediate site first to authorize the payment, then
        // redirected to the `return_url`.
        if (error.type === 'card_error' || error.type === 'validation_error') {
            utils.showMessage({form: event.target, message: error.message});

        } else {
            utils.showMessage({form: event.target, message: 'An unexpected error occurred.'});
        }

        utils.setLoading({form: event.target, isLoading: false});
    }
}

export default async function () {
    const cart = new Cart();
    cart.bind();

    const form = document.querySelector('.payments-checkout-form');
    if (!form) {
        return;
    }

    const checkout = await CheckoutForm.build(form as HTMLFormElement);
    await checkout.checkStatus();

    document.addEventListener('payments-pre-update-quantity', checkout.disable.bind(checkout));
    document.addEventListener('payments-update-quantity', checkout.update.bind(checkout));
    form.addEventListener('submit', checkout.submit.bind(checkout));
}